import { LANGUAGE_TAGS } from "@growthwise/core";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { HTML_LANG, localizedPath, splitLocalePath } from "../i18n";
import { useLanguage } from "../i18n/LanguageProvider";

interface SeoProps {
  titleKey: string;
  descriptionKey: string;
  /**
   * Keeps the page out of search results. Used for everything behind
   * sign-in: the account area, the admin console and the auth screens.
   */
  noIndex?: boolean;
}

/** Browser-tab title for every page, in every language. */
export const SITE_NAME = "GrowthWise Education";

const SITE_ORIGIN =
  typeof import.meta.env.VITE_PUBLIC_SITE_ORIGIN === "string" &&
  import.meta.env.VITE_PUBLIC_SITE_ORIGIN.length > 0
    ? import.meta.env.VITE_PUBLIC_SITE_ORIGIN.replace(/\/$/, "")
    : "";

/**
 * Per-page document head.
 *
 * Writes the title, description, canonical URL and the full set of
 * `hreflang` alternates. Each language has its own URL, so each needs its
 * own canonical pointing at itself and alternates pointing at its
 * translations -- otherwise search engines treat one language as a
 * duplicate of the other.
 *
 * The prerenderer renders these same tags into the static HTML, so a
 * crawler sees them without executing JavaScript.
 */
export const Seo = ({
  titleKey,
  descriptionKey,
  noIndex = false,
}: SeoProps) => {
  const { t, language } = useLanguage();
  const location = useLocation();

  const title = t(titleKey);
  const description = t(descriptionKey);

  useEffect(() => {
    document.title = SITE_NAME;
    setMeta("description", description);

    const { rest } = splitLocalePath(location.pathname);
    const canonicalPath = localizedPath(language, rest);

    setLink("canonical", `${SITE_ORIGIN}${canonicalPath}`);

    // Remove previous alternates before writing the current set, so
    // navigating between pages does not accumulate stale links.
    document
      .querySelectorAll('link[rel="alternate"][data-gw-hreflang]')
      .forEach((node) => node.remove());

    if (!noIndex) {
      for (const tag of LANGUAGE_TAGS) {
        appendAlternate(
          HTML_LANG[tag],
          `${SITE_ORIGIN}${localizedPath(tag, rest)}`,
        );
      }
      // x-default points at English for visitors whose language we do not
      // ship, rather than guessing.
      appendAlternate(
        "x-default",
        `${SITE_ORIGIN}${localizedPath("en", rest)}`,
      );
    }

    setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph, for link previews in messaging apps and social platforms.
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", `${SITE_ORIGIN}${canonicalPath}`, "property");
    setMeta("og:locale", language.replace("-", "_"), "property");
    setMeta("og:image", `${SITE_ORIGIN}/og-image.png`, "property");
    setMeta("twitter:card", "summary_large_image");
  }, [description, language, location.pathname, noIndex, title]);

  return null;
};

const setMeta = (
  name: string,
  content: string,
  attribute: "name" | "property" = "name",
): void => {
  let node = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${name}"]`,
  );
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attribute, name);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
};

const setLink = (rel: string, href: string): void => {
  let node = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", rel);
    document.head.appendChild(node);
  }
  node.setAttribute("href", href);
};

const appendAlternate = (hreflang: string, href: string): void => {
  const node = document.createElement("link");
  node.setAttribute("rel", "alternate");
  node.setAttribute("hreflang", hreflang);
  node.setAttribute("href", href);
  node.setAttribute("data-gw-hreflang", "true");
  document.head.appendChild(node);
};
